<template>
  <div class="settings--content email-template-page">
    <loading-state v-if="uiFlags.isFetching" />

    <form v-else class="row" @submit.prevent="saveTemplates">
      <div class="toolbar-row">
        <div class="templates__list-search toolbar-search">
          <fluent-icon icon="search" class="search-icon" size="16" />
          <input
            v-model="query"
            type="search"
            placeholder="Pesquisar templates"
            class="templates__search-input"
          />
        </div>

        <div class="actions-row">
          <button
            type="button"
            class="button button--primary"
            :disabled="!canSave"
            @click="saveTemplates"
          >
            Salvar templates
          </button>
          <button
            type="button"
            class="button button--primary"
            @click="addTemplate"
          >
            Criar Template
          </button>
          <button
            v-if="isCreatingTemplate"
            type="button"
            class="button button--secondary"
            @click="cancelCreate"
          >
            Cancelar
          </button>
        </div>
      </div>

      <div v-if="!isCreatingTemplate" class="template__list-container">
        <div v-if="!filteredTemplates.length" class="empty-state">
          Nenhum template encontrado.
        </div>

        <div
          v-for="(template, index) in filteredTemplates"
          :key="template.localId"
          class="template-list-card"
          :class="{
            'template-list-card--active':
              selectedTemplateId === template.localId,
            'template-list-card--expanded':
              expandedTemplateId === template.localId,
          }"
        >
          <div class="template-list-card__header">
            <div class="template-list-card__left">
              <div class="template-list-card__icon">T</div>
              <div class="template-list-card__content">
                <p class="template-list-card__title">
                  {{ template.name || `template_${index + 1}` }}
                </p>
                <p class="template-list-card__subtitle">
                  {{ template.subject || templatePreview(template) }}
                </p>
              </div>
            </div>

            <div class="template-list-card__actions">
              <template v-if="expandedTemplateId === template.localId">
                <button
                  type="button"
                  class="button button--secondary button--small"
                  @click.stop="cancelEditingTemplate(template.localId)"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="button button--primary button--small"
                  :disabled="!isTemplateComplete(template)"
                  @click.stop="saveTemplateChanges(template.localId)"
                >
                  Salvar
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="button button--primary button--small"
                  @click.stop="startEditingTemplate(template.localId)"
                >
                  Editar
                </button>
                <button
                  type="button"
                  class="button button--secondary button--small"
                  @click.stop="removeTemplateById(template.localId)"
                >
                  Excluir
                </button>
              </template>
            </div>
          </div>

          <div
            v-if="expandedTemplateId === template.localId"
            class="template-list-card__dropdown"
            @click.stop
          >
            <div class="field-group">
              <label class="field-label">Nome do template</label>
              <input
                v-model.trim="template.name"
                type="text"
                class="text-input"
                placeholder="Ex: followup_1"
              />
            </div>

            <div class="field-group">
              <label class="field-label">Assunto</label>
              <input
                v-model.trim="template.subject"
                type="text"
                class="text-input"
                placeholder="Ex: Atualização do seu atendimento"
              />
            </div>

            <div class="field-group">
              <label class="field-label">Conteúdo do Template</label>
              <textarea
                v-model="template.body"
                rows="6"
                class="textarea"
                placeholder="Digite o conteúdo do template"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isCreatingTemplate && selectedTemplate"
        class="template-editor"
      >
        <div class="template-editor__header">
          <p class="template-editor__title">Editar template</p>
          <woot-button
            icon="delete"
            variant="smooth"
            size="small"
            color-scheme="alert"
            class="button button--danger button--small delete-button"
            @click.stop="removeTemplateById(selectedTemplate.localId)"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Nome do template</label>
          <input
            v-model.trim="selectedTemplate.name"
            type="text"
            class="text-input"
            placeholder="Ex: followup_1"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Assunto</label>
          <input
            v-model.trim="selectedTemplate.subject"
            type="text"
            class="text-input"
            placeholder="Ex: Atualização do seu atendimento"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Conteúdo do Template</label>
          <textarea
            v-model="selectedTemplate.body"
            rows="6"
            class="textarea"
            placeholder="Digite o conteúdo do template"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';

const createEmptyTemplate = () => ({
  localId: `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`,
  name: '',
  subject: '',
  body: '',
});

export default {
  name: 'EmailTemplatePage',
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      templates: [],
      query: '',
      selectedTemplateId: null,
      expandedTemplateId: null,
      isCreatingTemplate: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    canSave() {
      return this.templates.every(
        template => template.name && template.subject && template.body
      );
    },
    selectedTemplate() {
      return (
        this.templates.find(
          template => template.localId === this.selectedTemplateId
        ) || null
      );
    },
    filteredTemplates() {
      const lowerCaseQuery = this.query.toLowerCase();
      return this.templates.filter(template => {
        const name = (template.name || '').toLowerCase();
        const subject = (template.subject || '').toLowerCase();
        const body = (template.body || '').toLowerCase();
        return (
          name.includes(lowerCaseQuery) ||
          subject.includes(lowerCaseQuery) ||
          body.includes(lowerCaseQuery)
        );
      });
    },
  },
  watch: {
    inbox: {
      immediate: true,
      deep: true,
      handler() {
        this.loadTemplatesFromInbox();
      },
    },
  },
  methods: {
    loadTemplatesFromInbox() {
      const templates =
        this.inbox?.email_templates ||
        this.inbox?.provider_config?.email_templates;
      if (!Array.isArray(templates) || !templates.length) {
        this.templates = [];
        this.selectedTemplateId = null;
        this.expandedTemplateId = null;
        this.isCreatingTemplate = false;
        return;
      }

      this.templates = templates.map(template => ({
        localId: `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`,
        name: template?.name || '',
        subject: template?.subject || '',
        body: template?.body || '',
      }));
      this.selectedTemplateId = null;
      this.expandedTemplateId = null;
      this.isCreatingTemplate = false;
    },
    addTemplate() {
      const newTemplate = createEmptyTemplate();
      this.templates.push(newTemplate);
      this.selectedTemplateId = newTemplate.localId;
      this.expandedTemplateId = null;
      this.isCreatingTemplate = true;
    },
    cancelCreate() {
      this.isCreatingTemplate = false;
      this.selectedTemplateId = null;
    },
    startEditingTemplate(localId) {
      this.selectedTemplateId = localId;
      this.expandedTemplateId = localId;
      this.isCreatingTemplate = false;
    },
    cancelEditingTemplate(localId) {
      if (this.expandedTemplateId === localId) {
        this.expandedTemplateId = null;
      }
      if (this.selectedTemplateId === localId) {
        this.selectedTemplateId = null;
      }
    },
    isTemplateComplete(template) {
      return !!(template?.name && template?.subject && template?.body);
    },
    async saveTemplateChanges(localId) {
      const template = this.templates.find(item => item.localId === localId);
      if (!this.isTemplateComplete(template)) {
        this.showAlert('Preencha nome, assunto e conteúdo antes de salvar.');
        return;
      }

      this.selectedTemplateId = localId;
      await this.saveTemplates();
      this.expandedTemplateId = null;
      this.selectedTemplateId = null;
    },
    templatePreview(template) {
      const content = (template?.body || '').trim();
      if (!content) return 'Sem conteúdo';
      return content.length > 120 ? `${content.slice(0, 120)}...` : content;
    },
    removeTemplateById(localId) {
      const removingCurrent = this.selectedTemplateId === localId;
      this.templates = this.templates.filter(
        template => template.localId !== localId
      );
      if (this.expandedTemplateId === localId) {
        this.expandedTemplateId = null;
      }
      if (removingCurrent) {
        this.selectedTemplateId = null;
      }
      if (!this.templates.length) {
        this.isCreatingTemplate = false;
      }
    },
    async saveTemplates() {
      try {
        const sanitizedTemplates = this.templates
          .map(template => ({
            name: template.name,
            subject: template.subject,
            body: template.body,
          }))
          .filter(
            template => template.name && template.subject && template.body
          );

        const providerConfig = {
          ...(this.inbox?.provider_config || {}),
          email_templates: sanitizedTemplates,
        };

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: providerConfig,
          },
        });

        this.showAlert('Templates de e-mail salvos com sucesso.');
      } catch (error) {
        this.showAlert(
          error.message || 'Não foi possível salvar os templates.'
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.email-template-page {
  .toolbar-row {
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--space-small);
    margin-bottom: var(--space-one);
  }

  .actions-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-small);
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .empty-state {
    padding: var(--space-medium);
    background: var(--s-50);
    border-radius: var(--border-radius-normal);
    color: var(--s-700);
    margin-bottom: var(--space-two);
  }

  .field-group {
    margin-top: var(--space-one);
  }

  .field-label {
    display: block;
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--space-micro);
    font-size: var(--font-size-mini);
  }

  .text-input,
  .textarea {
    width: 100%;
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-medium);
    padding: var(--space-small);
    background: var(--color-surface);
  }

  .templates__list-search {
    align-items: center;
    background-color: var(--s-25);
    border-radius: var(--border-radius-medium);
    border: 1px solid var(--s-100);
    display: flex;
    margin-bottom: var(--space-one);
    padding: 0 var(--space-one);

    .search-icon {
      color: var(--s-400);
    }

    .templates__search-input {
      background-color: transparent;
      border: none;
      font-size: var(--font-size-mini);
      height: unset;
      margin: 0;
      width: 100%;
    }
  }

  .toolbar-search {
    margin-bottom: 0;
    flex: 1;
  }

  .template__list-container {
    border-radius: var(--border-radius-medium);
    width: 100%;
    min-height: 100px;
    height: auto;
    max-height: none;
    overflow: visible;
    padding: var(--space-one);
  }

  .template-list-card {
    border-radius: var(--border-radius-medium);
    border: 1px solid var(--s-100);
    background: var(--color-surface);
    display: block;
    padding: var(--space-one);
    margin-bottom: var(--space-smaller);
    width: 100%;

    &:hover {
      background: var(--s-25);
    }
  }

  .template-list-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-one);
  }

  .template-list-card--expanded {
    background: var(--s-25);
  }

  .template-list-card--active {
    border-color: var(--b-400);
    background: var(--b-25);
  }

  .template-list-card__left {
    display: flex;
    align-items: center;
    gap: var(--space-one);
    min-width: 0;
    flex: 1;
  }

  .template-list-card__actions {
    display: flex;
    align-items: center;
    gap: var(--space-small);
  }

  .template-list-card__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 999px;
    background: var(--s-50);
    color: var(--b-600);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: var(--font-weight-medium);
    flex-shrink: 0;
  }

  .template-list-card__content {
    min-width: 0;
  }

  .template-list-card__title {
    margin: 0;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
  }

  .template-list-card__subtitle {
    margin: var(--space-micro) 0 0;
    color: var(--s-600);
    font-size: var(--font-size-mini);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    overflow: hidden;
  }

  .template-list-card__dropdown {
    width: 100%;
    margin-top: var(--space-one);
    padding-top: var(--space-one);
    border-top: 1px solid var(--s-100);
    color: var(--s-700);
    font-size: var(--font-size-mini);
    white-space: pre-wrap;
  }

  .template-editor {
    width: 100%;
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-medium);
    padding: var(--space-one);
    margin-top: var(--space-one);
    background: var(--color-surface);
  }

  .template-editor--placeholder {
    color: var(--s-600);
  }

  .template-editor__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .template-editor__title {
    margin: 0;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
  }

  .delete-button {
    display: flex;
    align-items: center;
    gap: var(--space-micro);
    padding: var(--space-micro) var(--space-small);
    font-size: var(--font-size-mini);
  }
}
</style>
